import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

import NavigationSidebar from '../../components/NavigationSidebar';
import { Building2, Users, Plus, ArrowLeft } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';


const TeamManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamSlug, setTeamSlug] = useState('');
  const [maxSeats, setMaxSeats] = useState(5);
  const [activeSection, setActiveSection] = useState('members');

  const handleOrganizationNameChange = (value) => {
    setOrganizationName(value);
    // Auto-generate slug
    const slug = value?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '');
    setOrganizationSlug(slug);
  };

  const handleTeamNameChange = (value) => {
    setTeamName(value);
    // Auto-generate slug
    const slug = value?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '');
    setTeamSlug(slug);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      // Create organization
      const { data: org, error: orgError } = await supabase
        ?.from('organizations')
        ?.insert({
          name: organizationName,
          slug: organizationSlug,
          owner_id: user?.id
        })
        ?.select()
        ?.single();

      if (orgError) throw orgError;

      // Create team
      const { data: team, error: teamError } = await supabase
        ?.from('teams')
        ?.insert({
          organization_id: org?.id,
          name: teamName,
          slug: teamSlug,
          max_seats: maxSeats,
          subscription_tier: 'free'
        })
        ?.select()
        ?.single();

      if (teamError) throw teamError;

      // Add current user as owner
      const { error: memberError } = await supabase
        ?.from('team_members')
        ?.insert({
          team_id: team?.id,
          user_id: user?.id,
          role: 'owner'
        });

      if (memberError) throw memberError;

      toast?.success('Team created successfully!');
      navigate('/workspace-switcher');
    } catch (error) {
      console.error('Error creating team:', error);
      toast?.error(error?.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
      <NavigationSidebar />
      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/workspace-switcher')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Workspaces
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-2">
                <img 
                  src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                  alt="DraftIQ Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Create New Team</h1>
                <p className="text-gray-600">Set up your organization and team workspace</p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  1
                </div>
                <span className={`text-sm font-medium ${
                  step >= 1 ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  Organization
                </span>
              </div>
              <div className="flex-1 h-1 mx-4 bg-gray-200">
                <div className={`h-full transition-all duration-300 ${
                  step >= 2 ? 'bg-purple-600 w-full' : 'bg-gray-200 w-0'
                }`} />
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  2
                </div>
                <span className={`text-sm font-medium ${
                  step >= 2 ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  Team Details
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Organization Details</h2>
                    <p className="text-sm text-gray-600">Your organization can have multiple teams</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name *
                  </label>
                  <Input
                    type="text"
                    value={organizationName}
                    onChange={(e) => handleOrganizationNameChange(e?.target?.value)}
                    placeholder="Acme Corporation"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Slug *
                  </label>
                  <Input
                    type="text"
                    value={organizationSlug}
                    onChange={(e) => setOrganizationSlug(e?.target?.value)}
                    placeholder="acme-corporation"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used in URLs and must be unique
                  </p>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!organizationName || !organizationSlug}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Team Details</h2>
                    <p className="text-sm text-gray-600">Create your first team workspace</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Name *
                  </label>
                  <Input
                    type="text"
                    value={teamName}
                    onChange={(e) => handleTeamNameChange(e?.target?.value)}
                    placeholder="Engineering Team"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Slug *
                  </label>
                  <Input
                    type="text"
                    value={teamSlug}
                    onChange={(e) => setTeamSlug(e?.target?.value)}
                    placeholder="engineering-team"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used in URLs and must be unique within your organization
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Seats
                  </label>
                  <Input
                    type="number"
                    value={maxSeats}
                    onChange={(e) => setMaxSeats(parseInt(e?.target?.value))}
                    min="1"
                    max="100"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of team members that can join (can be increased later)
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    loading={loading}
                    className="flex-1"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Team
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• You will be added as the team owner</li>
              <li>• You can invite team members via email</li>
              <li>• All team data will be isolated and secure</li>
              <li>• You can upgrade to Enterprise for SSO and advanced features</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;